import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import { getActivityLog, getActivityLogFilterOptions } from '../../services/reportApi';
import { formatDateTime } from '../../utils/dateUtils';

const ActivityLog = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [filterOptions, setFilterOptions] = useState({ users: [], subject_types: [] });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', user_id: '', subject_type: '' });
    const [showFilters, setShowFilters] = useState(false);

    const fetchData = useCallback(async (pg = 1, perPage = entriesPerPage) => {
        setLoading(true); setError(null);
        try {
            const res = await getActivityLog({ ...filters, page: pg, per_page: perPage });
            if (res.success) {
                setData(res.data.data || []);
                setTotal(res.data.total || 0);
                setLastPage(res.data.last_page || 1);
                setPage(res.data.current_page || pg);
            }
        } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, [filters, entriesPerPage]);

    useEffect(() => {
        fetchData(1, entriesPerPage);
        getActivityLogFilterOptions().then(r => { if (r.success) setFilterOptions(r.data); }).catch(() => {});
    }, []);
    useEffect(() => { fetchData(1, entriesPerPage); }, [entriesPerPage]);

    const handleFilterApply = () => { fetchData(1, entriesPerPage); setShowFilters(false); };
    const handleFilterReset = () => setFilters({ start_date: '', end_date: '', user_id: '', subject_type: '' });

    const filteredData = data.filter(r =>
        (r.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.subject_type_label || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleExport = (format) => {
        const headers = ['Date', 'User', 'Activity', 'Subject', 'IP Address'];
        const rows = filteredData.map(r => [
            r.created_at ? formatDateTime(r.created_at) : '',
            r.user_name || '', r.description || '',
            r.subject_type_label || '', r.ip_address || ''
        ]);
        const title = 'Activity Log'; const filename = 'activity_log';
        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
        setShowExportDropdown(false);
    };

    const from = total === 0 ? 0 : (page - 1) * entriesPerPage + 1;
    const to = Math.min(page * entriesPerPage, total);
    const inputStyle = { padding: '0.35rem 0.6rem', border: '1px solid #e2e8f0', borderRadius: '5px', fontSize: '0.8rem', outline: 'none', background: '#fff' };
    const selStyle = { ...inputStyle, cursor: 'pointer' };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Filters ── */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setShowFilters(v => !v)} style={{ ...inputStyle, cursor: 'pointer', background: showFilters ? '#3b82f6' : '#fff', color: showFilters ? '#fff' : '#334155', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <i className="fa fa-filter" /> Filters
                </button>
                {showFilters && (<>
                    <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                    <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />
                    <select value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} style={selStyle}>
                        <option value="">All Users</option>
                        {filterOptions.users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                    </select>
                    <select value={filters.subject_type} onChange={e => setFilters(f => ({ ...f, subject_type: e.target.value }))} style={selStyle}>
                        <option value="">All Activities</option>
                        {filterOptions.subject_types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <button onClick={handleFilterApply} style={{ ...inputStyle, background: '#3b82f6', color: '#fff', cursor: 'pointer', border: 'none' }}>Apply</button>
                    <button onClick={handleFilterReset} style={{ ...inputStyle, cursor: 'pointer' }}>Reset</button>
                </>)}
            </div>

            {/* ── Table Container ── */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid #e2e8f0', padding: '1rem 1.25rem' }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>
                    <div className="table-controls-right">
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                                    {[{ icon: 'fa-file-excel-o', label: 'Export CSV', action: () => handleExport('CSV') }, { icon: 'fa-file-excel-o', label: 'Export Excel', action: () => handleExport('Excel') }, { icon: 'fa-print', label: 'Print', action: () => handleExport('Print') }, { icon: 'fa-file-pdf-o', label: 'Export PDF', action: () => handleExport('PDF') }].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: '#475569' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <i className={`fa ${item.icon}`} style={{ width: '14px', textAlign: 'center' }} />{item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input type="text" placeholder="Search ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px' }} />
                    </div>
                </div>

                {error && <div style={{ padding: '1rem', color: '#ef4444', fontSize: '0.875rem' }}>Error: {error}</div>}

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Date', 'User', 'Activity', 'Subject', 'IP Address'].map(h => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No data available in table</td></tr>
                            ) : filteredData.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569', whiteSpace: 'nowrap' }}>{row.created_at ? formatDateTime(row.created_at) : ''}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.user_name}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.description}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>
                                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' }}>
                                            {row.subject_type_label || row.subject_type}
                                        </span>
                                    </td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#64748b' }}>{row.ip_address}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
                        {total > 0 ? `Showing ${from} to ${to} of ${total} entries` : 'No entries'}
                    </div>
                    <div className="pagination-controls">
                        <button className={`page-btn${page <= 1 ? ' disabled' : ''}`} onClick={() => { if (page > 1) fetchData(page - 1); }} disabled={page <= 1}>Previous</button>
                        {Array.from({ length: Math.min(lastPage, 5) }, (_, i) => { const pg = page <= 3 ? i + 1 : page - 2 + i; if (pg < 1 || pg > lastPage) return null; return <button key={pg} className={`page-btn${pg === page ? ' active' : ''}`} onClick={() => fetchData(pg)}>{pg}</button>; })}
                        <button className={`page-btn${page >= lastPage ? ' disabled' : ''}`} onClick={() => { if (page < lastPage) fetchData(page + 1); }} disabled={page >= lastPage}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivityLog;
